import React, { useEffect, useState } from 'react';

import { Checkpoint, TrackedSession } from '../../../../../../types/common.types';

import { isCheckpointConfigured } from '../../../../../../utils/accountability.util';
import { ARCHIVED } from '../../../../../../utils/constants';
import { generateUUID, getClassNames } from '../../../../../../utils/util';

import Button from '../../../../../common/button/Button';
import TextInput from '../../../../../common/input/text-input/TextInput';
import Modal from '../../../../../common/modal/Modal';
import AccountabilityConfigureCheckpointModal from '../accountability-configure-checkpoint-modal/AccountabilityConfigureCheckpointModal';

import { ReactComponent as DeleteIcon } from '../../../../../../resources/icons/close.svg';

import './accountability-checkpoints-modal.scss';

type Props = {
  status: string;
  show: boolean;
  close: () => void;
  session?: TrackedSession;
  updateSession: (session: TrackedSession) => void;
};

const AccountabilityCheckpointsModal = ({ status, show, close, session, updateSession }: Props) => {
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [showCheckpointModal, setShowCheckpointModal] = useState(false);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<Checkpoint>();

  useEffect(() => {
    if (session) {
      setCheckpoints(structuredClone(session.checkpoints));
    } else {
      setCheckpoints([]);
    }
  }, [session, show]);

  if (!session) return null;

  const handleCheckpointNameChange = (checkpoint: Checkpoint, name: string) => {
    const copy = structuredClone(checkpoints);
    const index = copy.findIndex((c) => c.uuid === checkpoint.uuid);
    if (index !== -1) {
      copy[index]!.name = name;
      setCheckpoints(copy);
    }
  };

  const handleDeleteClick = (checkpoint: Checkpoint) => {
    setCheckpoints((prev) => prev.filter((c) => c.uuid !== checkpoint.uuid));
  };

  const handleConfigureClick = (checkpoint: Checkpoint) => {
    setShowCheckpointModal(true);
    setSelectedCheckpoint(checkpoint);
  };

  const handleUpdateCheckpoint = (checkpoint: Checkpoint) => {
    setCheckpoints((prev) => prev.map((c) => (c.uuid === checkpoint.uuid ? checkpoint : c)));
  };

  const handleAddClick = () => {
    setCheckpoints((prev) => [
      ...prev,
      {
        uuid: generateUUID(),
        isNew: true,
        name: '',
        deviceId: '',
        checkpointPasses: [],
        manualStartTime: false,
        multiplePassesConfigured: false
      }
    ]);
  };

  const closeModal = () => {
    close();
    setCheckpoints([]);
  };

  const handleSaveClick = () => {
    updateSession({
      ...session,
      checkpoints
    });
    closeModal();
  };

  const renderCheckpoints = () => {
    return checkpoints.map((checkpoint) => {
      return (
        <div key={checkpoint.uuid} className="accountability-checkpoints-modal__checkpoint">
          <TextInput
            value={checkpoint.name}
            onChange={({ target }) => handleCheckpointNameChange(checkpoint, target.value)}
            name={checkpoint.uuid}
            disabled={status === ARCHIVED}
          />
          {status !== ARCHIVED && (
            <button
              className="accountability-checkpoints-modal__delete-button"
              onClick={() => handleDeleteClick(checkpoint)}
            >
              <DeleteIcon data-testid="delete-icon" />
            </button>
          )}
          <Button
            className="accountability-checkpoints-modal__configure-button"
            text={status === ARCHIVED ? 'View' : 'Configure'}
            onClick={() => handleConfigureClick(checkpoint)}
          />
          <div
            className={getClassNames('accountability-checkpoints-modal__checkpoint-status', {
              complete: isCheckpointConfigured(checkpoint)
            })}
          />
        </div>
      );
    });
  };

  return (
    <>
      <Modal show={show && !showCheckpointModal}>
        <div className="accountability-checkpoints-modal">
          <div className="accountability-checkpoints-modal__header">Checkpoints</div>
          <div className="accountability-checkpoints-modal__content">
            <div className="accountability-checkpoints-modal__checkpoints">
              {checkpoints.length > 0 && <p>Checkpoint Names</p>}
              {renderCheckpoints()}
              {status !== ARCHIVED && (
                <Button className="accountability-checkpoints-modal__add-button" text="Add" onClick={handleAddClick} />
              )}
            </div>
          </div>
          <div className="accountability-checkpoints-modal__button-container">
            <Button text={status === ARCHIVED ? 'Close' : 'Cancel'} onClick={closeModal} />
            {status !== ARCHIVED && <Button text="Save" onClick={handleSaveClick} />}
          </div>
        </div>
      </Modal>

      <AccountabilityConfigureCheckpointModal
        status={status}
        show={showCheckpointModal}
        close={() => setShowCheckpointModal(false)}
        checkpoint={selectedCheckpoint}
        updateCheckpoint={handleUpdateCheckpoint}
      />
    </>
  );
};

export default AccountabilityCheckpointsModal;
